import { useState, useEffect, useCallback } from 'react';
import { cn, formatCurrency, formatDuration, formatDayOfWeek } from '@/lib/utils';
import type { User, DayOfWeek } from '@/types';
import { FAMILY_ROLE_HOURLY_RATE } from '@/types';
import type { UserStats } from '@/types';
import { getFullName } from '@/types';
import { useUserStats } from '@/hooks/useUsers';
import { useAuth } from '@/hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { familyPaymentsApi } from '@/lib/api';
import { Avatar } from '@/components/common';
import { RoleBadge, AdminBadge } from './RoleBadge';
import { DollarSign, Check, Clock } from 'lucide-react';

interface PersonDetailProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
}

// Format hours to X.X hours
function formatHours(hours: number | undefined | null): string {
  if (hours === undefined || hours === null) {
    return '0.0 hours';
  }
  return formatDuration(hours);
}

// Format currency with fallback
function formatMoney(amount: number | undefined | null): string {
  if (amount === undefined || amount === null) {
    return '$0.00';
  }
  return formatCurrency(amount);
}

// Format days list
function formatDaysList(days: DayOfWeek[] | undefined): string {
  if (!days || days.length === 0) {
    return 'None';
  }
  return days.map(formatDayOfWeek).join(', ');
}

export function PersonDetail({ user, isOpen, onClose }: PersonDetailProps) {
  const { data: stats, isLoading: statsLoading } = useUserStats(
    user?.role === 'caretaker' ? user?.id : undefined
  );

  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !user) {
    return null;
  }

  const fullName = getFullName(user.first_name, user.last_name);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-over panel */}
      <div
        data-testid="container-person-detail"
        className={cn(
          'fixed inset-y-0 right-0 z-50',
          'w-full max-w-md',
          'bg-background border-l border-slate-700',
          'shadow-2xl shadow-black/50',
          'transform transition-transform duration-300 ease-out',
          'flex flex-col',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="person-detail-title"
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar
                firstName={user.first_name}
                lastName={user.last_name}
                profilePictureUrl={user.profile_picture_url}
                role={user.role}
                size="xl"
              />
              <div>
                <h2
                  id="person-detail-title"
                  data-testid="container-person-detail-name"
                  className="text-xl font-semibold text-white"
                >
                  {fullName}
                </h2>
                <div data-testid="container-person-detail-role" className="mt-2 flex flex-wrap gap-2">
                  <RoleBadge
                    role={user.role}
                    otherLabel={user.other_role_label}
                    size="md"
                  />
                </div>
              </div>
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={onClose}
              data-testid="clickable-person-detail-close"
              className={cn(
                'p-2 rounded-lg',
                'text-slate-400 hover:text-white',
                'hover:bg-slate-700/50',
                'transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-accent'
              )}
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {user.role === 'caretaker' && (
            <CaretakerDetails user={user} stats={stats} statsLoading={statsLoading} />
          )}
          {user.role === 'parent' && (
            <ParentDetails user={user} />
          )}
          {user.role === 'family' && <FamilyDetails user={user} />}
          {user.role === 'professional' && <ProfessionalDetails user={user} />}
          {user.role === 'admin' && (
            <AdminDetails user={user} />
          )}
        </div>
      </div>
    </>
  );
}

interface CaretakerDetailsProps {
  user: User;
  stats?: UserStats | null;
  statsLoading: boolean;
}

function CaretakerDetails({ user, stats, statsLoading }: CaretakerDetailsProps) {
  return (
    <div className="space-y-6">
      {/* Work Details */}
      <section>
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">
          Work Details
        </h3>
        <div className="space-y-3">
          <DetailRow
            label="Hourly Wage"
            value={formatMoney(user.hourly_wage)}
          />
          <DetailRow
            label="Max Hours/Month"
            value={user.max_hours_per_month ? `${user.max_hours_per_month} hours` : 'Not set'}
          />
        </div>
      </section>

      {/* Schedule Preferences */}
      <section>
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">
          Schedule Preferences
        </h3>
        <div className="space-y-3">
          <DetailRow
            label="Preferred Days Off"
            value={formatDaysList(user.preferred_days_off)}
          />
          <DetailRow
            label="Unavailable Days"
            value={formatDaysList(user.unavailable_days)}
          />
        </div>
      </section>

      {/* Stats Card */}
      <section>
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">
          Statistics
        </h3>
        {statsLoading ? (
          <div className="grid grid-cols-2 gap-3">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="p-4 rounded-lg bg-card border border-slate-700/50 animate-pulse"
              >
                <div className="h-4 bg-slate-700 rounded w-3/4 mb-2" />
                <div className="h-6 bg-slate-700 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : (
          <div data-testid="container-person-detail-stats" className="grid grid-cols-2 gap-3">
            <StatCard
              label="Hours This Month"
              value={formatHours(stats?.hours_this_month)}
            />
            <StatCard
              label="Total Hours"
              value={formatHours(stats?.hours_all_time)}
            />
            <StatCard
              label="Earned This Month"
              value={formatMoney(stats?.earned_this_month)}
            />
            <StatCard
              label="Total Earned"
              value={formatMoney(stats?.earned_all_time)}
            />
          </div>
        )}
      </section>
    </div>
  );
}

interface ParentDetailsProps {
  user: User;
}

function ParentDetails({ user }: ParentDetailsProps) {
  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">
          Contact Information
        </h3>
        <div className="space-y-3">
          <DetailRow label="Email" value={user.email} />
          <DetailRow label="Role" value="Guardian" />
        </div>
      </section>
    </div>
  );
}

interface FamilyDetailsProps {
  user: User;
}

function FamilyDetails({ user }: FamilyDetailsProps) {
  const { user: currentUser } = useAuth();
  const isGuardian = currentUser?.role === 'parent';

  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">
          Details
        </h3>
        <div className="space-y-3">
          <DetailRow label="Email" value={user.email} />
          <DetailRow label="Hourly Rate" value={`$${FAMILY_ROLE_HOURLY_RATE}/hr`} />
        </div>
      </section>

      {isGuardian && <FamilyPaymentSection userId={user.id} firstName={user.first_name} />}
    </div>
  );
}

export function FamilyPaymentSection({ userId, firstName }: { userId: string; firstName: string }) {
  const queryClient = useQueryClient();
  const [selectedSlotIds, setSelectedSlotIds] = useState<string[]>([]);

  const { data: paymentData, isLoading } = useQuery({
    queryKey: ['family-payments', userId],
    queryFn: () => familyPaymentsApi.getPayments(userId),
    staleTime: 1000 * 5,
  });

  const markPaidMutation = useMutation({
    mutationFn: (slotIds: string[]) => familyPaymentsApi.markPaid(slotIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-payments', userId] });
      setSelectedSlotIds([]);
    },
  });

  const handleToggleSlot = (slotId: string) => {
    setSelectedSlotIds((prev) =>
      prev.includes(slotId) ? prev.filter((id) => id !== slotId) : [...prev, slotId]
    );
  };

  const handleSelectAll = () => {
    if (!paymentData) return;
    const allIds = paymentData.unpaid.slots.map((s: any) => s.id);
    setSelectedSlotIds(allIds);
  };

  const handleMarkPaid = () => {
    if (selectedSlotIds.length === 0) return;
    markPaidMutation.mutate(selectedSlotIds);
  };

  if (isLoading) {
    return (
      <section>
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">
          Payment Tracking
        </h3>
        <div className="animate-pulse space-y-3">
          <div className="h-20 bg-slate-700 rounded-lg" />
          <div className="h-20 bg-slate-700 rounded-lg" />
        </div>
      </section>
    );
  }

  if (!paymentData) return null;

  const { unpaid, paid } = paymentData;

  return (
    <>
      {/* Unpaid Section */}
      <section data-testid="container-family-payment-unpaid">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
            <Clock size={14} />
            Unpaid
          </h3>
          {unpaid.slots.length > 0 && (
            <span data-testid="container-family-payment-unpaid-amount" className="text-sm font-semibold text-amber-400">
              ${unpaid.total_amount.toFixed(2)} owed
            </span>
          )}
        </div>

        {unpaid.slots.length === 0 ? (
          <div data-testid="container-family-payment-empty" className="p-4 rounded-lg bg-card border border-slate-700/50 text-center">
            <p className="text-gray-400 text-sm">All caught up! No unpaid shifts.</p>
          </div>
        ) : (
          <>
            {/* Summary card */}
            <div data-testid="container-family-payment-summary" className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-3">
              <div className="flex justify-between items-center">
                <div>
                  <div data-testid="container-family-payment-total" className="text-amber-400 font-semibold text-lg">${unpaid.total_amount.toFixed(2)}</div>
                  <div data-testid="container-family-payment-hours" className="text-amber-400/70 text-xs">{unpaid.total_hours.toFixed(1)} hours unpaid</div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSelectAll}
                    data-testid="clickable-family-payment-select-all"
                    className="text-xs px-2 py-1 rounded bg-slate-700 text-gray-300 hover:bg-slate-600 transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleMarkPaid}
                    disabled={selectedSlotIds.length === 0 || markPaidMutation.isPending}
                    data-testid="clickable-family-payment-mark-paid"
                    className="text-xs px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  >
                    <Check size={12} />
                    {markPaidMutation.isPending ? 'Saving...' : `Mark Paid (${selectedSlotIds.length})`}
                  </button>
                </div>
              </div>
            </div>

            {/* Unpaid slot list */}
            <div data-testid="container-family-payment-unpaid-list" className="space-y-1.5 max-h-60 overflow-y-auto">
              {unpaid.slots.map((slot: any) => (
                <label
                  key={slot.id}
                  data-testid={`clickable-family-payment-slot-${slot.id}`}
                  className={cn(
                    'flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors',
                    selectedSlotIds.includes(slot.id)
                      ? 'bg-emerald-500/10 border-emerald-500/40'
                      : 'bg-card border-slate-700/50 hover:border-slate-600'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selectedSlotIds.includes(slot.id)}
                    onChange={() => handleToggleSlot(slot.id)}
                    data-testid={`toggle-family-payment-slot-${slot.id}`}
                    className="accent-emerald-500 w-4 h-4"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-medium">{slot.date}</div>
                    <div className="text-gray-400 text-xs">
                      {formatTime12h(slot.start_time)} - {formatTime12h(slot.end_time)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-white text-sm font-medium">${slot.pay_amount.toFixed(2)}</div>
                    <div className="text-gray-400 text-xs">{slot.hours_worked.toFixed(1)}h</div>
                  </div>
                </label>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Paid Section */}
      {paid.slots.length > 0 && (
        <section data-testid="container-family-payment-paid">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <DollarSign size={14} />
              Paid
            </h3>
            <span data-testid="container-family-payment-paid-amount" className="text-sm text-emerald-400">
              ${paid.total_amount.toFixed(2)} total
            </span>
          </div>

          <div data-testid="container-family-payment-paid-list" className="space-y-1.5 max-h-40 overflow-y-auto">
            {paid.slots.map((slot: any) => (
              <div
                key={slot.id}
                data-testid={`container-family-payment-paid-slot-${slot.id}`}
                className="flex items-center gap-2 p-2.5 rounded-lg bg-card border border-slate-700/50 opacity-70"
              >
                <Check size={14} className="text-emerald-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm">{slot.date}</div>
                  <div className="text-gray-400 text-xs">
                    {formatTime12h(slot.start_time)} - {formatTime12h(slot.end_time)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white text-sm">${slot.pay_amount.toFixed(2)}</div>
                  <div className="text-gray-400 text-xs">{slot.hours_worked.toFixed(1)}h</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function formatTime12h(time: string): string {
  if (!time || !time.includes(':')) return time || '';
  const [hours, minutes] = time.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return time;
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

interface ProfessionalDetailsProps {
  user: User;
}

function ProfessionalDetails({ user }: ProfessionalDetailsProps) {
  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">
          Details
        </h3>
        <div className="space-y-3">
          <DetailRow
            label="Occupation"
            value={user.other_role_label || 'Not specified'}
          />
          <DetailRow label="Email" value={user.email} />
        </div>
      </section>
    </div>
  );
}

interface AdminDetailsProps {
  user: User;
}

function AdminDetails({ user }: AdminDetailsProps) {
  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3">
          Contact Information
        </h3>
        <div className="space-y-3">
          <DetailRow label="Email" value={user.email} />
          <DetailRow
            label="Access Level"
            value={
              <span className="text-amber-400 font-medium">Administrator</span>
            }
          />
        </div>
      </section>
    </div>
  );
}

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-slate-700/50 last:border-0">
      <span className="text-slate-400">{label}</span>
      <span className="text-white font-medium">{value}</span>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: string;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <div className="p-4 rounded-lg bg-card border border-slate-700/50">
      <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">
        {label}
      </div>
      <div className="text-lg font-semibold text-white">{value}</div>
    </div>
  );
}

export default PersonDetail;
